import { useEffect, useState } from "react";
import { Plus, Trash2, Repeat } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { money, fmtDate, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DatePicker } from "@/components/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const NONE = "__none__";
const EMPTY = { source: "", amount: "", cadence: "monthly", date: todayISO(), note: "", job_id: NONE };

export default function RecurringIncome() {
  const { currentId } = useCompany();
  const [rows, setRows] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/recurring").then((r) => setRows(r.data));
  useEffect(() => {
    if (currentId) {
      load();
      api.get("/jobs").then((r) => setJobs(r.data));
    }
  }, [currentId]);

  const save = async () => {
    const amount = parseFloat(form.amount);
    if (!form.source.trim()) return toast.error("Source is required");
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    try {
      await api.post("/recurring", { ...form, amount, job_id: form.job_id === NONE ? null : form.job_id });
      toast.success("Recurring income logged");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error("Could not save");
    }
  };

  const remove = async (id) => {
    await api.delete(`/recurring/${id}`);
    load();
  };

  const totalNet = rows.reduce((s, r) => s + r.amount, 0);
  const totalGst = rows.reduce((s, r) => s + r.gst_amount, 0);

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Recurring Income</h1>
          <p className="text-slate-500 text-sm mt-1">
            Revenue billed elsewhere (e.g. external Stripe) — recorded here so it counts toward GST.
          </p>
        </div>
        <Button data-testid="add-recurring-btn" onClick={() => { setForm(EMPTY); setOpen(true); }} className="rounded-md bg-slate-900 hover:bg-slate-800">
          <Plus className="h-4 w-4 mr-2" /> Log Income
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6 max-w-md">
        <Card className="p-4 rounded-lg border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-wide text-slate-400">Net Recorded</div>
          <div className="font-heading text-2xl font-bold tabular-nums">{money(totalNet)}</div>
        </Card>
        <Card className="p-4 rounded-lg border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-wide text-slate-400">GST On It</div>
          <div className="font-heading text-2xl font-bold tabular-nums">{money(totalGst)}</div>
        </Card>
      </div>

      <Card className="rounded-lg border-slate-200 shadow-sm overflow-hidden p-0">
        {rows.length === 0 ? (
          <div className="p-16 text-center">
            <Repeat className="h-8 w-8 mx-auto text-slate-300" />
            <p className="text-slate-400 mt-3 text-sm">No recurring income logged yet.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="recurring-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                <th className="px-5 py-3 font-medium">Source</th>
                <th className="px-5 py-3 font-medium">Date</th>
                <th className="px-5 py-3 font-medium">Cadence</th>
                <th className="px-5 py-3 font-medium text-right">Amount (net)</th>
                <th className="px-5 py-3 font-medium text-right">GST (5%)</th>
                <th className="px-5 py-3 font-medium text-right"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="px-5 py-3 font-medium text-slate-900">
                    {r.source}
                    {r.note && <span className="block text-xs text-slate-400">{r.note}</span>}
                  </td>
                  <td className="px-5 py-3 text-slate-600">{fmtDate(r.date)}</td>
                  <td className="px-5 py-3">
                    <Badge variant="outline" className="capitalize bg-slate-50 text-slate-600 border-slate-200">
                      {r.cadence}
                    </Badge>
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">{money(r.amount)}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-slate-600">{money(r.gst_amount)}</td>
                  <td className="px-5 py-3 text-right">
                    <Button variant="ghost" size="icon" data-testid={`delete-recurring-${r.id}`} onClick={() => remove(r.id)}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading">Log Recurring Income</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Source</Label>
              <Input
                data-testid="recurring-source-input"
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                placeholder="e.g. SaaS subscription — Acme Co"
                className="mt-1 rounded-md"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (net, pre-GST)</Label>
                <Input
                  type="number"
                  step="0.01"
                  data-testid="recurring-amount-input"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="mt-1 rounded-md"
                />
              </div>
              <div>
                <Label>Cadence</Label>
                <Select value={form.cadence} onValueChange={(v) => setForm({ ...form, cadence: v })}>
                  <SelectTrigger data-testid="recurring-cadence-select" className="mt-1 rounded-md capitalize">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Date</Label>
              <div className="mt-1">
                <DatePicker testId="recurring-date-picker" value={form.date} onChange={(d) => setForm({ ...form, date: d })} />
              </div>
            </div>
            <div>
              <Label>Note</Label>
              <Input
                data-testid="recurring-note-input"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="mt-1 rounded-md"
              />
            </div>
            <div>
              <Label>Job (optional)</Label>
              <Select value={form.job_id} onValueChange={(v) => setForm({ ...form, job_id: v })}>
                <SelectTrigger data-testid="recurring-job-select" className="mt-1 rounded-md">
                  <SelectValue placeholder="No job" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  <SelectItem value={NONE}>No job</SelectItem>
                  {jobs.map((j) => (
                    <SelectItem key={j.id} value={j.id}>{j.code ? `${j.code} · ` : ""}{j.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-400">GST is auto-calculated at 5% of the net amount.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-recurring-btn" onClick={save} className="rounded-md bg-slate-900 hover:bg-slate-800">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
